import { Reducer } from 'redux';

import { Nullable } from '@common/typescript/objects/Nullable';
import { List } from '@common/typescript/objects/List';
import { WithId } from '@common/typescript/objects/WithId';

import { DeliveryTypeEntity } from '@app/objects/DeliveryType';
import { AppThunkAction, ApplicationState } from '@app/store/index';
import { request } from '@app/components/Api';

/**
 * Per-crematory map of delivery types
*/
export interface DeliveryTypeRecord {
	map: Map<number, DeliveryTypeEntity>;
	state: 'querying' | 'mutating' | 'idle';
	error: Nullable<string>;
	total: number;
	controller?: AbortController;
}

function getDefaultRecord(): DeliveryTypeRecord {
	return {
		map: new Map<number, DeliveryTypeEntity>(),
		state: 'idle',
		error: null,
		total: 0,
		controller: undefined,
	};
}

/**
 * Root DeliveryType store containing record for multiple crematories
 */
export type DeliveryTypeState = Partial<Record<number, DeliveryTypeRecord>>;

export enum ActionKeys {
	REQUESTDELIVERYTYPES = 'REQUESTDELIVERYTYPES',
	RECEIVEDELIVERYTYPES = 'RECEIVEDELIVERYTYPES',
	RECEIVEDELIVERYTYPESERROR = 'RECEIVEDELIVERYTYPESERROR',
	MUTATEDELIVERYTYPES = 'MUTATEDELIVERYTYPES',
}

interface PayloadBase {
	crematoryId: number;
}

interface RequestPayload extends PayloadBase {
	controller: AbortController;
}

interface RequestAction {
	type: ActionKeys.REQUESTDELIVERYTYPES;
	payload: RequestPayload;
}

interface ReceiveErrorPayload extends PayloadBase {
	error: Nullable<string>;
}

interface ReceiveErrorAction {
	type: ActionKeys.RECEIVEDELIVERYTYPESERROR;
	payload: ReceiveErrorPayload;
}

interface ReceiveListPayload extends PayloadBase {
	list: List<DeliveryTypeEntity>;
}

interface ReceiveListAction {
	type: ActionKeys.RECEIVEDELIVERYTYPES;
	payload: ReceiveListPayload;
}

interface MutatePayload extends PayloadBase {
	controller: AbortController;
}

interface MutateItemAction {
	type: ActionKeys.MUTATEDELIVERYTYPES;
	payload: MutatePayload;
}

type KnownActions = RequestAction
	| ReceiveErrorAction
	| ReceiveListAction
	| MutateItemAction;

type ActionResult = AppThunkAction<KnownActions>
type GetState = () => ApplicationState;
type Dispatch = (action: KnownActions) => void;

type InsertData = Omit<DeliveryTypeEntity, 'id'> & { id: -1, deleted: never };
type UpdateData = Partial<DeliveryTypeEntity> & WithId;
type DeleteData = WithId & { deleted: true };
interface MutateData extends PayloadBase {
	item: InsertData | UpdateData | DeleteData;
}

export interface ActionCreators {
	request: (force?: boolean) => ActionResult;
	mutate: (mutation: MutateData) => ActionResult;
}

const fetchParams = {
	count: 100,
	offset: 0,
};

export function getActionCreators(crematoryId: number): ActionCreators {
	const createController = (getState: GetState): AbortController => {
		const state = getState();
		const record = state.deliveryTypes[crematoryId];

		record?.controller?.abort();
		const controller = new AbortController();

		// This is a tricky thing. We should not mutate record directly
		// We do not need this field to rerender site.
		// But we do need any feature requests to cancel this one as soon as possible
		if (record) {
			record.controller = controller;
		}

		return controller;
	};

	/**
	 * Make a request to get delivery types for a particular crematory
	 * @param {boolean} force force request. By default if data is already fetched - it would not be refetched
	 * @param {Dispatch} dispatch Redux dispatch function
	 * @param {GetState} getState Function returning current redux state
	 * @returns {Promise<void>}
	 */
	const query = (force: boolean, dispatch: Dispatch, getState: GetState) => {
		const state = getState();
		const record = state.deliveryTypes[crematoryId];

		if (!force && record) return Promise.resolve();

		const controller = createController(getState);

		dispatch({
			type: ActionKeys.REQUESTDELIVERYTYPES,
			payload: {
				crematoryId,
				controller,
			},
		});

		return request<List<DeliveryTypeEntity>>('deliveryTypeList', fetchParams, state, controller.signal)
			.then((list: List<DeliveryTypeEntity>) => {
				dispatch({
					type: ActionKeys.RECEIVEDELIVERYTYPES,
					payload: {
						crematoryId,
						list,
					},
				});
			})
			.catch((error: Error) => {
				dispatch({
					type: ActionKeys.RECEIVEDELIVERYTYPESERROR,
					payload: {
						crematoryId,
						error: error?.message,
					},
				});
			});
	};

	return {
		request: (force: boolean = false): ActionResult => (dispatch: Dispatch, getState: GetState) => query(force, dispatch, getState),

		mutate(mutation: MutateData): ActionResult {
			return (dispatch: Dispatch, getState: GetState) => {
				const controller = createController(getState);
				const state = getState();

				dispatch({
					type: ActionKeys.MUTATEDELIVERYTYPES,
					payload: {
						crematoryId,
						controller,
					},
				});

				let task: Promise<void>;

				if (mutation.item.id < 0) {
					// Add new mutation
					task = request('createDeliveryType', mutation.item, state, controller.signal);
				} else if ((mutation.item as DeleteData).deleted) {
					task = request('deleteDeliveryType', mutation.item, state, controller.signal);
				} else {
					task = request('updateDeliveryType', mutation.item, state, controller.signal);
				}

				return task
					.then(() => query(true, dispatch, getState))
					.catch((error: Error) => {
						dispatch({
							type: ActionKeys.RECEIVEDELIVERYTYPESERROR,
							payload: {
								crematoryId,
								error: error?.message,
							},
						});
					});
			};
		},
	};
}

function mutateState(state: DeliveryTypeState, crematoryId: number, record: DeliveryTypeRecord): DeliveryTypeState {
	return {
		...state,
		[crematoryId]: record,
	};
}

function mutateRecord(source: DeliveryTypeRecord | undefined, mutation: Partial<DeliveryTypeRecord>): DeliveryTypeRecord {
	const record = source ?? getDefaultRecord();

	return {
		...record,
		...mutation,
	};
}

function toMap(list: List<DeliveryTypeEntity>): Map<number, DeliveryTypeEntity> {
	return list.list.reduce(
		(acc: Map<number, DeliveryTypeEntity>, cur: DeliveryTypeEntity) => acc.set(cur.id, cur),
		new Map<number, DeliveryTypeEntity>(),
	);
}

export function getReducer(): Reducer<DeliveryTypeState, KnownActions> {
	return (state: DeliveryTypeState = {}, action: KnownActions) => {
		switch (action.type) {
		case ActionKeys.REQUESTDELIVERYTYPES: {
			const record = state[action.payload.crematoryId];
			const mutation: Partial<DeliveryTypeRecord> = {
				state: 'querying',
				controller: action.payload.controller,
			};
			const mutated = mutateRecord(record, mutation);

			return mutateState(state, action.payload.crematoryId, mutated);
		}

		case ActionKeys.RECEIVEDELIVERYTYPESERROR: {
			const record = state[action.payload.crematoryId];
			const mutation: Partial<DeliveryTypeRecord> = {
				state: 'idle',
				error: action.payload.error,
			};
			const mutated = mutateRecord(record, mutation);

			return mutateState(state, action.payload.crematoryId, mutated);
		}

		case ActionKeys.RECEIVEDELIVERYTYPES: {
			const record = state[action.payload.crematoryId];
			const mutation: Partial<DeliveryTypeRecord> = {
				state: 'idle',
				total: action.payload.list.count,
				map: toMap(action.payload.list),
				error: null,
			};
			const mutated = mutateRecord(record, mutation);

			return mutateState(state, action.payload.crematoryId, mutated);
		}
		case ActionKeys.MUTATEDELIVERYTYPES: {
			const record = state[action.payload.crematoryId];
			const mutation: Partial<DeliveryTypeRecord> = {
				state: 'mutating',
				controller: action.payload.controller,
			};
			const mutated = mutateRecord(record, mutation);

			return mutateState(state, action.payload.crematoryId, mutated);
		}

		default: return state;
		}
	};
}
